"use client";

import React from "react";
import type { Communications } from "@/src/gql/graphql";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";

// Import communications detail config
import { getEntityDetailConfig } from "@/lib/entity-registry";

// Import communications detail tab components
import { CommunicationsDetailTabComponents } from "@/features/tasks/components/CommunicationsDetailTabComponents";

// Dispatch each tab through the shared component map by its `customComponent`
// key (generic resolution, NOT a hand-maintained switch) so a tab declared in
// the detail config can never silently fall through to the default.
const COMMUNICATIONS_TAB_COMPONENTS = CommunicationsDetailTabComponents as Record<
  string,
  React.ComponentType<TabContentProps<Communications>>
>;

function resolveCommunicationsTabComponent(
  tab: TabConfig<Communications> | undefined
): React.ComponentType<TabContentProps<Communications>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && COMMUNICATIONS_TAB_COMPONENTS[custom]) {
    return COMMUNICATIONS_TAB_COMPONENTS[custom];
  }
  return COMMUNICATIONS_TAB_COMPONENTS.CommunicationsDetailsDisplay;
}

// Import info summary and inline edit components
import { CommunicationsInfoSummary } from "@/features/tasks/components/CommunicationsInfoSummary";
import { CommunicationsInlineEditPanel } from "@/features/tasks/components/CommunicationsInlineEditPanel";

// ============================================================================
// COMMUNICATIONS DETAIL MODAL TABS HOOK
// ============================================================================

export interface UseCommunicationsDetailModalTabsReturn {
  tabs: TabConfig<Communications>[];
  renderPrimaryArea: (activeTab: string, communication: Communications) => React.ReactNode;
  renderInfoPanel: (communication: Communications) => React.ReactNode;
  renderInlineInfoPanel: (
    communication: Communications,
    onInlineEdit: (communication: Communications) => Promise<void>
  ) => React.ReactNode;
}

export function useCommunicationsDetailModalTabs(): UseCommunicationsDetailModalTabsReturn {
  // Get tabs from config (static for now)
  const tabs = getEntityDetailConfig<Communications>("communications").tabs;

  // Render primary area based on active tab
  const renderPrimaryArea = (activeTab: string, communication: Communications): React.ReactNode => {
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];

    const TabComponent = resolveCommunicationsTabComponent(tabConfig);
    return <TabComponent item={communication} tab={tabConfig} />;
  };

  // Render info panel fallback for layouts without inline panel support.
  const renderInfoPanel = (communication: Communications): React.ReactNode => {
    return (
      <div className="space-y-4">
        <CommunicationsInfoSummary item={communication} />
      </div>
    );
  };

  const renderInlineInfoPanel = (
    communication: Communications,
    onInlineEdit: (updatedCommunication: Communications) => Promise<void>
  ): React.ReactNode => {
    const handleUpdate = async (updates: Partial<Communications>) => {
      await onInlineEdit({
        ...communication,
        ...updates,
      });
    };

    return (
      <div className="space-y-4">
        <CommunicationsInfoSummary item={communication} />
        <CommunicationsInlineEditPanel item={communication} onUpdate={handleUpdate} />
      </div>
    );
  };

  return {
    tabs,
    renderPrimaryArea,
    renderInfoPanel,
    renderInlineInfoPanel,
  };
}
